import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { useHeroToast } from '../../../../hooks';
import { findFormat, findPlatform } from '../../config/platforms';
import { useLicensePreviews, useSaveCreative } from '../../hooks';
import { SavedKind, useAdsGeneratorStore } from '../../model/store';
import { copyElements, hasClipboard, pasteElements } from '../../utils/clipboard';
import { buildFileName, ExportFormat } from '../../utils/export';
import { Inspector } from '../Inspector';
import { AdStage, AdStageHandle } from '../Stage/AdStage';
import { AiComposer } from './AiComposer';
import { EditorBody, EditorFooter, EditorShell, InspectorColumn, PanelColumn, StageArea } from './Editor.styled';
import { EditorTopBar } from './EditorTopBar';
import { LicenseAssetsModal } from './LicenseAssetsModal';
import {
  BackgroundPanel,
  ElementsPanel,
  ImagesPanel,
  LayersPanel,
  ShapesPanel,
  StockPanel,
  TemplatesPanel,
  TextPanel,
} from './panels';
import { SaveCreativeModal } from './SaveCreativeModal';
import { EditorTool, ToolRail } from './ToolRail';

interface EditorProps {
  onBack: () => void;
}

export const Editor: FC<EditorProps> = ({ onBack }) => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const zoom = useAdsGeneratorStore((state) => state.zoom);
  const setZoom = useAdsGeneratorStore((state) => state.setZoom);
  const saved = useAdsGeneratorStore((state) => state.saved);
  const { showError } = useHeroToast();

  const shellRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<AdStageHandle>(null);
  const [activeTool, setActiveTool] = useState<EditorTool>('templates');
  const [isFitMode, setIsFitMode] = useState(true);
  const [saveDialog, setSaveDialog] = useState<{ kind: SavedKind; asNew: boolean } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  /** Zamówienie eksportu wstrzymane do czasu dociągnięcia licencji. */
  const [blockedExport, setBlockedExport] = useState<{ format: ExportFormat; scale: number } | null>(null);

  const { save, isSaving } = useSaveCreative(stageRef);
  const licensing = useLicensePreviews(scene);
  const [isExporting, setIsExporting] = useState(false);

  // Skróty klawiszowe działają na aktualnym stanie ze store'u, więc nie trzeba
  // przepinać nasłuchu przy każdej zmianie zaznaczenia.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;

      const state = useAdsGeneratorStore.getState();
      const isMeta = event.metaKey || event.ctrlKey;

      if (isMeta && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) state.redo();
        else state.undo();
        return;
      }

      const { selectedIds } = state;

      // Zaznacz wszystko — po tym działają już akcje zbiorcze poniżej.
      if (isMeta && event.key.toLowerCase() === 'a' && state.scene) {
        event.preventDefault();
        state.selectMany(state.scene.elements.filter((item) => !item.locked).map((item) => item.id));
        return;
      }

      // Wklejanie działa też bez zaznaczenia, więc sprawdzamy je wcześniej.
      if (isMeta && event.key.toLowerCase() === 'v' && hasClipboard() && state.scene) {
        event.preventDefault();
        const offset = Math.round(Math.min(state.scene.width, state.scene.height) * 0.03);
        state.addElements(pasteElements(offset));
        return;
      }

      if (selectedIds.length === 0) return;

      if (isMeta && event.key.toLowerCase() === 'c' && state.scene) {
        event.preventDefault();
        copyElements(state.scene.elements.filter((item) => selectedIds.includes(item.id)));
        return;
      }

      if (isMeta && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        selectedIds.forEach((id) => state.duplicateElement(id));
        return;
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        selectedIds.forEach((id) => state.removeElement(id));
        return;
      }

      const step = event.shiftKey ? 10 : 1;
      const delta: Record<string, { x: number; y: number }> = {
        ArrowLeft: { x: -step, y: 0 },
        ArrowRight: { x: step, y: 0 },
        ArrowUp: { x: 0, y: -step },
        ArrowDown: { x: 0, y: step },
      };
      const move = delta[event.key];
      if (move) {
        event.preventDefault();
        state.commit(`nudge-${selectedIds.join('-')}`);
        selectedIds.forEach((id) => {
          const element = state.scene?.elements.find((item) => item.id === id);
          if (element) {
            state.updateElement(id, { x: element.x + move.x, y: element.y + move.y }, true);
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Stan pełnego ekranu bierzemy z przeglądarki, a nie z własnego przełącznika:
  // wyjście klawiszem Escape nie przechodzi przez nasz przycisk.
  useEffect(() => {
    const sync = (): void => setIsFullscreen(document.fullscreenElement === shellRef.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await shellRef.current?.requestFullscreen();
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Przeglądarka nie pozwoliła włączyć pełnego ekranu');
    }
  }, [showError]);

  const exportFile = useCallback(async (format: ExportFormat, scale: number) => {
    if (!scene) return;

    const stage = stageRef.current;
    if (!stage) {
      showError('Plansza nie jest gotowa do zapisu. Odśwież stronę i spróbuj ponownie.');
      return;
    }

    setIsExporting(true);
    try {
      await stage.download({
        format,
        scale,
        fileName: buildFileName(
          [
            findPlatform(scene.platformId)?.name ?? 'kreacja',
            findFormat(scene.platformId, scene.formatId)?.label ?? '',
            `${scene.width}x${scene.height}`,
          ],
          format,
        ),
      });
    } catch (error) {
      console.error('[AdsGenerator] eksport kreacji nie powiódł się', error);
      showError(error instanceof Error ? error.message : 'Nie udało się wygenerować pliku');
    } finally {
      setIsExporting(false);
    }
  }, [scene, showError]);

  /**
   * Kreacji z roboczym podglądem ze stocka nie da się wyeksportować, więc
   * zamiast błędu proponujemy dociągnięcie licencjonowanych plików i dopiero
   * potem przygotowujemy plik.
   */
  const handleDownload = (format: ExportFormat, scale: number): void => {
    if (scene && licensing.previews.length > 0) {
      setBlockedExport({ format, scale });
      return;
    }
    exportFile(format, scale).catch(() => undefined);
  };

  const platform = scene ? findPlatform(scene.platformId) : undefined;
  const format = scene ? findFormat(scene.platformId, scene.formatId) : undefined;

  /**
   * Zapis w miejscu (nadpisanie otwartej kreacji) idzie bez pytania o nazwę —
   * nazwa już jest. O nazwę pytamy tylko wtedy, gdy powstaje nowy wpis.
   */
  const handleSave = (kind: SavedKind, asNew?: boolean): void => {
    if (!asNew && saved && saved.kind === kind) {
      save({ name: saved.name, kind });
      return;
    }
    setSaveDialog({ kind, asNew: Boolean(asNew) });
  };

  const handleZoomStep = (factor: number): void => {
    setIsFitMode(false);
    setZoom(zoom * factor);
  };

  /** Krok AI ma własną nakładkę na planszy, więc nie dostaje panelu bocznego. */
  const panels: Partial<Record<EditorTool, ReactNode>> = {
    templates: <TemplatesPanel onGenerate={() => setActiveTool('ai')} />,
    text: <TextPanel />,
    elements: <ElementsPanel />,
    shapes: <ShapesPanel />,
    images: <ImagesPanel />,
    stock: <StockPanel />,
    background: <BackgroundPanel />,
    layers: <LayersPanel />,
  };
  const activePanel = panels[activeTool];

  return (
    <EditorShell ref={shellRef}>
      <EditorTopBar
        onBack={onBack}
        onSave={handleSave}
        isSaving={isSaving}
        onDownload={handleDownload}
        isExporting={isExporting}
        onFit={() => setIsFitMode(true)}
        onZoomStep={handleZoomStep}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
      />
      <EditorBody>
        <ToolRail active={activeTool} onChange={setActiveTool} />
        {activePanel && <PanelColumn>{activePanel}</PanelColumn>}
        <StageArea>
          <AdStage ref={stageRef} isFitMode={isFitMode} />
          {activeTool === 'ai' && <AiComposer onClose={() => setActiveTool('templates')} />}
        </StageArea>
        <InspectorColumn>
          <Inspector />
        </InspectorColumn>
      </EditorBody>

      {blockedExport && (
        <LicenseAssetsModal
          previews={licensing.previews}
          isLicensing={licensing.isLicensing}
          progress={licensing.progress}
          onClose={() => setBlockedExport(null)}
          onConfirm={async () => {
            const licensed = await licensing.licenseAll();
            if (!licensed) return;
            const request = blockedExport;
            setBlockedExport(null);
            await exportFile(request.format, request.scale);
          }}
        />
      )}

      {saveDialog && (
        <SaveCreativeModal
          isOpen
          kind={saveDialog.kind}
          isSaving={isSaving}
          defaultName={saved?.name || `${platform?.name ?? 'Kreacja'} ${format?.label ?? ''}`.trim()}
          onClose={() => setSaveDialog(null)}
          onConfirm={async (name) => {
            const done = await save({ name, kind: saveDialog.kind, asNew: saveDialog.asNew });
            if (done) setSaveDialog(null);
          }}
        />
      )}

      <EditorFooter>
        <span>Podwójne kliknięcie tekstu edytuje treść, Ctrl+Z cofa zmianę</span>
        <span>
          Shift+klik zaznacza kolejne, Alt+przeciągnij duplikuje, Ctrl+C i Ctrl+V kopiują, Ctrl+A zaznacza wszystko
        </span>
      </EditorFooter>
    </EditorShell>
  );
};
